import React, { useMemo } from 'react';
import { get, map } from 'lodash';
import { FormikHelpers } from 'formik';
import { SelectField } from '@shared/fields/SelectField';
import { SwitchField } from '@shared/fields/SwitchField';
import { getOptions } from '@shared/commonFunctions';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { TextInputField } from '@shared/fields/TextInputField';
import { IRole, IQueue } from '../../QueueConfiguration/types';
import { TAnyItem, IMenuItem, IMenuValues, TMenuItemFormConfig, EMenuItemType, IMenuItemIconOption } from '../types';
import { MENU_ITEM_TYPE_OPTIONS, menuItemIconOptions } from '../constants';
import { getMenuItemColors } from '../helpers';
import { IconOptionLabel } from '../components/IconOptionLabel';

interface IConfigProps {
  menuItemPrefix: string | null;
  roleOptions: IRole[];
  queueOptions: IQueue[];
  menuItemsValues: IMenuItem[];
  setFieldValue: FormikHelpers<IMenuValues>['setFieldValue'];
}

interface IFormConfigData {
  menuItemFormConfig: TMenuItemFormConfig;
}

export const useMenuItemFormConfig = ({
  menuItemPrefix,
  roleOptions,
  queueOptions,
  menuItemsValues,
  setFieldValue,
}: IConfigProps): IFormConfigData => {
  const menuItemFormConfig = useMemo<TMenuItemFormConfig>(() => {
    if (!menuItemPrefix) {
      return [];
    }

    const selectedItemValues: TAnyItem = get(menuItemsValues, menuItemPrefix.replace('menuItems', ''), {} as TAnyItem);

    const { menuItemType, hasAlert } = selectedItemValues;

    const itemTypeConfig: TMenuItemFormConfig =
      menuItemType === EMenuItemType.Expandable
        ? []
        : [
            {
              width: '100%',
              name: 'menuItemType',
              label: 'Type',
              component: SelectField,
              options: MENU_ITEM_TYPE_OPTIONS,
              hideNoneOption: true,
              onChange: (value: EMenuItemType) => {
                const updatedItem: TAnyItem = {
                  ...selectedItemValues,
                  menuItemType: value,
                  ...('childMenuItems' in selectedItemValues && {
                    childMenuItems: value === EMenuItemType.Expandable ? selectedItemValues.childMenuItems : [],
                  }),
                };

                setFieldValue(menuItemPrefix, { ...updatedItem, ...getMenuItemColors(updatedItem) });
              },
            },
          ];

    const alertConfig: TMenuItemFormConfig =
      hasAlert
        ? [
          {
            width: '100%',
            name: 'alertCount',
            label: 'Alert Count',
            type: 'number',
            component: TextInputField,
          },
        ]
        : [];

    const queueConfig: TMenuItemFormConfig =
      menuItemType === EMenuItemType.Queue
        ? [
            {
              width: '100%',
              name: 'queueId',
              label: 'Queue',
              component: SelectAutocompleteField,
              options: getOptions(queueOptions, 'id', 'subgroup', 'group'),
            },
            {
              name: 'hasAlert',
              component: SwitchField,
              onChange: (checked: boolean) => {
                const updatedItem: TAnyItem = {
                  ...selectedItemValues,
                  hasAlert: checked,
                };

                setFieldValue(menuItemPrefix, {
                  ...updatedItem,
                  ...getMenuItemColors(updatedItem),
                  alertCount: !checked ? 0 : updatedItem.alertCount
                });
              },
            },
            ...alertConfig,
          ]
        : [];

    const linkConfig: TMenuItemFormConfig =
      menuItemType === EMenuItemType.Link
        ? [
            {
              width: '100%',
              name: 'link',
              label: 'Link',
            },
          ]
        : [];

    const itemConfig: TMenuItemFormConfig = [
      {
        width: '100%',
        name: 'text',
        label: 'Text',
      },
      {
        width: '100%',
        name: 'iconClass',
        label: 'Icon Class',
        component: SelectAutocompleteField,
        options: menuItemIconOptions,
        renderOption: (props: React.HTMLAttributes<HTMLLIElement>, { value }: IMenuItemIconOption) => (
          <IconOptionLabel {...props} icon={value} />
        ),
      },
      ...itemTypeConfig,
      ...queueConfig,
      ...linkConfig,
      {
        width: '100%',
        name: 'roleIds',
        label: 'Roles',
        component: SelectAutocompleteField,
        options: getOptions(roleOptions),
        multiple: true,
      },
    ];

    return map(itemConfig, ({ name, ...config }) => ({
      ...config,
      name: `${menuItemPrefix}.${name}`,
    })) as TMenuItemFormConfig;
  }, [menuItemPrefix, roleOptions, queueOptions, menuItemsValues, setFieldValue]);

  return { menuItemFormConfig };
};
